import {
  Button,
  ChevronDownIcon,
  ChevronEndIcon,
  Dialog,
  EditIcon,
  Flex,
  FormLabel,
  Input,
  Loader,
  MeetingTimeIcon,
  ProviderConsumer as FluentUIThemeConsumer,
  Text,
  TrashCanIcon
} from '@fluentui/react-northstar';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import EmptySVG from '../../../../svg/empty.svg';
import AppCSS from '../../../App.module.css';
import { TicketDatePicker } from '../../common/Controls/TicketDateTimePicker';
import { EmptyData } from '../EmptyData';
import { NestedDropdown } from '../NestedDropdown/NestedDropdown';
import { platformService } from '../../services/platform.service';
import { getTicketSettings } from '../../cache/TicketSettingsCache';
import { themeNames } from '@fluentui/react-teams';
import { Toast } from '../toast/Toast';
import { UserAvatar } from '../UserAvatar';
import { DurationText } from './DurationText';
import { ShareBar } from './ShareBar';
import Styles from './TicketTimeTracking.module.css';
import { splitDuration } from './durationFormatter';
import {
  clientDay, entrySortKey, formatEntryDate, formatEntryWhen,
  fromEntryDate, fromEntryInstant, isSameLocalDay, localDayOf, nowAsTime, quarterHours,
  snapDownToQuarter, toEntryDate, toEntryInstant
} from './entryDate';

interface TicketTimeTrackingProps {
  ticketId: number;
  /** Platform user id of the signed-in user; owns which entries they may change. */
  currentUserId: number;
  /** Admins may edit or delete anyone's entry; everyone else only their own. */
  isAdmin: boolean;
  /**
   * Closed or Deflected - the two states Ticket.Closed covers.
   *
   * The tab stays visible and the breakdown still expands, because a finished ticket is
   * exactly the one people read time off. Everything that would change a total is
   * disabled instead, admins included: time is logged against work, and there is no more
   * work to do.
   */
  isTicketClosed: boolean;
  /**
   * Fired with the ticket's new total whenever entries are (re)loaded, so the page header
   * can follow along without a refresh. Optional - the tab works standalone without it.
   *
   * `changedHere` says whether this page caused the change. False when the load merely
   * picked up what someone else logged - a total the header should show, but must not
   * stamp as a modification of its own.
   */
  onTotalChanged?: (totalMinutes: number, changedHere: boolean) => void;
}

interface TimeEntry {
  Id: number;
  TicketId: number;
  UserId: number;
  Minutes: number;
  EntryDate: string;
  /** Whether EntryDate's time of day is real. See TicketTimeEntry.cs. */
  HasTimeOfDay?: boolean;
  User?: {
    Id: number;
    FullName?: string;
    GivenName?: string;
    FamilyName?: string;
    // UserAvatar resolves the cached photo from one of these; without it the avatar
    // silently falls back to initials.
    AadObjectId?: string;
    Email?: string;
  };
}

const MAX_MINUTES = 60;


/**
 * An HH:mm value as the viewer's locale writes it - 2:30 PM or 14:30.
 *
 * Only the quarter-hour list needs this. The native input formats itself from the browser
 * and OS, and cannot be told otherwise; every label in the list is ours, so it has to be
 * formatted deliberately or it would read 24-hour to everyone.
 */
const labelForTime = (time: string): string => {
  const [h, m] = time.split(':').map(Number);
  return new Date(2000, 0, 1, h, m).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};

/**
 * The nearest quarter at or below a minutes value, as the list writes it.
 *
 * Down, matching snapDownToQuarter for the start time: rounding a duration up would credit
 * time nobody worked.
 */
const snapMinutesToQuarter = (value: string): string =>
  String(Math.floor((parseInt(value, 10) || 0) / 15) * 15);

/**
 * The 96 quarter-hours of the day, with their labels, built once.
 *
 * Every value is fixed, so rebuilding this per render did 96 allocations on each keystroke in
 * Hours or Minutes for a result that cannot differ. The LABEL is the half worth hoisting:
 * labelForTime constructs a Date and formats it through Intl, which is far dearer than the
 * string it replaces, and it was still running per option per render after the values were
 * hoisted.
 *
 * Safe at module scope because labelForTime passes `undefined` as the locale, which resolves to
 * the browser's, and that does not change within a session.
 *
 * Shaped for NestedDropdown's Item rather than for the <option> elements this used to build:
 * `value` is the label it renders, and `time` carries the "HH:mm" the form actually stores,
 * which is not the same string. See the control itself for why it is no longer a <select>.
 *
 * `Value` and `ParentId` are not duplicates worth deleting. NestedDropdown builds the list
 * from the lowercase Item fields but builds its TRIGGER TEXT in parentData(), which reads the
 * capitalised entity names - the ticket form happens to supply those by spreading the raw
 * entity into its dataItem. An item carrying only the lowercase fields selects and highlights
 * correctly and renders a blank trigger, which is exactly how this was noticed.
 */
const QUARTER_HOUR_OPTIONS = quarterHours().map((time, i) => {
  const label = labelForTime(time);
  return { id: i, parentId: null, position: i, value: label, ParentId: null, Value: label, time };
});

/** The four quarters, in the same shape. The id IS the minute count, so it needs no lookup. */
const QUARTER_MINUTE_OPTIONS = [0, 15, 30, 45].map((m, i) => {
  const label = String(m).padStart(2, '0');
  return { id: m, parentId: null, position: i, value: label, ParentId: null, Value: label };
});

/** Shared, so an agent with no entries does not produce a fresh array on every render. */
const EMPTY_ENTRIES: TimeEntry[] = [];

const displayName = (user: TimeEntry['User']): string => {
  if (!user) return '';
  if (user.FullName) return user.FullName;
  return [user.GivenName, user.FamilyName].filter(Boolean).join(' ');
};

/** Same calendar day in the user's own timezone, which is the one they booked against. */
const isToday = (date: Date) => isSameLocalDay(date);

/** Midnight tonight - anything at or past this is a future date. */
const endOfToday = () => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
};

const TicketTimeTrackingImpl: React.FC<TicketTimeTrackingProps> = ({ ticketId, currentUserId, isAdmin, isTicketClosed, onTotalChanged }) => {
  const { t } = useTranslation();

  // One per mount, not one per render. platformService defines its ~289 methods as arrow
  // class properties, so each `new` allocates a closure for every one of them - and this ran
  // on every keystroke in Hours or Minutes. The constructor body itself is two assignments;
  // the methods are the cost. A stable reference also means it can safely be named in a
  // dependency array later, which a fresh object per render never could.
  const api = useMemo(() => new platformService(), []);

  const [entries, setEntries] = useState<TimeEntry[]>([]);
  /**
   * The ticket this instance is currently showing. The component is not remounted when
   * the user moves to another ticket - only the prop changes - so a request started for
   * the previous one is still in flight and will resolve into this same instance.
   * Compared after every await to decide whether the response is still wanted.
   */
  const shownTicketId = useRef<number | null>(ticketId);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [entryDate, setEntryDate] = useState<Date>(new Date());
  /** Individual entry being edited, or null when adding new time. */
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  /** Agent rows currently expanded to show their individual entries. */
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [pendingDelete, setPendingDelete] = useState<{ userId: number; name: string } | null>(null);
  const [pendingEntryDelete, setPendingEntryDelete] = useState<TimeEntry | null>(null);

  /**
   * Tenant setting: time may only be booked against today. Fetched here rather than in
   * EditTicket's parallel load because this tab is only mounted when someone opens it -
   * putting it there would cost every ticket view a round trip for a setting most of them
   * never read.
   */
  const [restrictToToday, setRestrictToToday] = useState(false);
  const [captureStartTime, setCaptureStartTime] = useState(false);
  const [restrictToQuarters, setRestrictToQuarters] = useState(false);

  // Local HH:mm. Defaults to now, snapped down when the tenant restricts increments -
  // rounding up would put the start a few minutes into the future.
  const [startTime, setStartTime] = useState<string>(() => nowAsTime(false));

  /**
   * @param changedHere Whether this page caused the change. False on the initial load,
   * which reports what the server already had - possibly time another agent logged while
   * this ticket sat open. The total is reported either way; the flag only decides whether
   * the page also stamps the ticket as modified, which opening a tab must not do.
   */
  const loadEntries = async (changedHere = false) => {
    // Captured before the await. Switching tickets quickly leaves two requests in flight,
    // and they can come back in either order - so responses are matched against the ticket
    // on screen when they land, not the one that was on screen when they were sent.
    // Without this, a slow response for the previous ticket paints its entries, its total
    // and its loading state over the ticket the user is actually looking at.
    const requestedTicketId = ticketId;
    const isStale = () => shownTicketId.current !== requestedTicketId;

    try {
      const params = `?$filter=TicketId eq ${requestedTicketId}&$expand=User($select=FullName,GivenName,FamilyName,AadObjectId,Email)&$orderby=EntryDate desc`;
      const loaded = (await api.getTicketTimeEntries(params)) ?? [];
      if (isStale()) return;

      setEntries(loaded);
      // Reported from here rather than from each add/edit/delete handler, so every path
      // that changes the total tells the page about it exactly once, and the number the
      // header shows is the one the server just returned rather than one we predicted.
      //
      // Reported on the initial load too. The tab is unmounted when the agent switches
      // away from it, so coming back runs this - and it is the only point at which the
      // header learns about time another agent logged while the ticket was open.
      onTotalChanged?.(loaded.reduce((sum, e) => sum + (e.Minutes ?? 0), 0), changedHere);
    } catch {
      // A failure that belongs to a ticket the user has left is not theirs to see.
      if (isStale()) return;
      Toast.error(t('time-tracking.error.load-failed'));
    } finally {
      // Guarded too: the effect sets isLoading for the incoming ticket, and a late
      // response for the outgoing one would otherwise clear it and show an empty tab.
      if (!isStale()) setIsLoading(false);
    }
  };

  // Through the shared cache rather than a fetch of its own: the rule editor reads the same
  // object, and this ran once per ticket the agent opened for a setting that changes about
  // never. getTicketSettings never rejects - a failed read resolves to the unrestricted
  // shape, which is what the try/catch here used to fall back to.
  //
  // The cost is that a change made ELSEWHERE - another admin, another tab - is seen up to an
  // hour late rather than on the next ticket. This app's own saves are pushed straight in by
  // setCachedTicketSettings, so an admin never sees their own change lag.
  //
  // CaptureStartTime is the field where a stale copy actually costs something, and it is worth
  // naming precisely. Stale-false while the server says true means whenToStore sends a bare
  // midnight UTC while MarkTimeOfDay flags the row HasTimeOfDay - the flagged-but-timeless row
  // that reads back as 5:00 PM the previous day west of UTC. The other two are benign: a stale
  // current-day rule just earns a server rejection, and a stale quarter-hour rule lets an
  // off-grid duration through, which display tolerates by design.
  //
  // Accepted rather than fixed. Shaun's call, 2026-09-02: CaptureStartTime is set once at
  // rollout and left, so the exposure is an agent saving inside the same hour the toggle is
  // first flipped. If that ever stops being true, the fix is a maxAge argument on
  // getTicketSettings - the tab asking for a fresher copy than the rule editor needs.
  const loadRestriction = async () => {
    // Guarded the same way loadEntries is, and for the same reason: this awaits, and the
    // component is not remounted when the user moves to another ticket - it is unmounted
    // outright when they leave the tab. The effect's cleanup nulls shownTicketId, so a
    // settings response landing after that would otherwise set state on a dead component.
    //
    // The window is usually small now that getTicketSettings is cached, but it is widest
    // exactly when it matters: the very first load, before anything is cached.
    const requestedTicketId = ticketId;
    const config = await getTicketSettings();
    if (shownTicketId.current !== requestedTicketId) return;

    setRestrictToToday(config.RestrictTimeEntryToCurrentDay);
    setCaptureStartTime(config.CaptureStartTime);
    setRestrictToQuarters(config.RestrictTimeEntryToQuarterHours);
    // Re-seeded once the setting is known, so the default lands on the grid the picker
    // will actually offer rather than a value it cannot show.
    setStartTime(nowAsTime(config.RestrictTimeEntryToQuarterHours));
  };

  useEffect(() => {
    shownTicketId.current = ticketId;
    setIsLoading(true);
    resetForm();
    loadEntries();
    loadRestriction();

    // Cleared on unmount as well as on every ticket change, so a response arriving after
    // the tab has gone is discarded rather than setting state on a dead component.
    return () => {
      shownTicketId.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  /**
   * Holds Minutes and Start Time on the quarter-hour grid while the tenant restricts them.
   *
   * The two controls become lists when the restriction is on, and a list can only display one
   * of the options it was given - so a value off the grid has nothing to select and the browser
   * falls back to showing the first option. Deriving only what is *rendered* left the form
   * showing 00 while it still held, and saved, the 7 minutes an older entry was loaded with.
   * The number behind the control has to move too, which is what this does.
   *
   * Off-grid values are not hypothetical: they are every entry logged before the tenant turned
   * the restriction on, loaded back by startEdit.
   *
   * Depends on the values themselves rather than on the settings, so there is no path in -
   * loading an entry, clearing the form, the setting arriving late - that can leave one behind.
   * Both snaps are idempotent, so this settles after one pass instead of looping.
   */
  useEffect(() => {
    if (!restrictToQuarters) return;

    const snappedMinutes = snapMinutesToQuarter(minutes);
    if (snappedMinutes !== minutes) setMinutes(snappedMinutes);

    const snappedStart = snapDownToQuarter(startTime);
    if (snappedStart !== startTime) setStartTime(snappedStart);
  }, [restrictToQuarters, minutes, startTime]);

  /**
   * The list entries the two quarter-hour pickers are currently sitting on.
   *
   * Snapped first, because the form's own value is free-form until the effect above rounds it -
   * an entry loaded with 7 minutes has to show as 00 rather than as nothing while that settles.
   * undefined when nothing matches, which the controls render as an empty trigger rather than
   * as a stale label.
   */
  const startTimeOption = QUARTER_HOUR_OPTIONS.find(o => o.time === snapDownToQuarter(startTime));
  const minutesOption = QUARTER_MINUTE_OPTIONS.find(o => String(o.id) === snapMinutesToQuarter(minutes));

  const totalMinutes = useMemo(() => entries.reduce((sum, e) => sum + (e.Minutes || 0), 0), [entries]);

  /** Per-person totals, ordered by most time logged. Matches the design's right pane. */
  const perPerson = useMemo(() => {
    const byUser = new Map<number, { name: string; idOrUpn: string; minutes: number }>();
    entries.forEach(entry => {
      const existing = byUser.get(entry.UserId);
      if (existing) existing.minutes += entry.Minutes || 0;
      else
        byUser.set(entry.UserId, {
          name: displayName(entry.User),
          idOrUpn: entry.User?.AadObjectId || entry.User?.Email || '',
          minutes: entry.Minutes || 0
        });
    });
    return [...byUser.entries()]
      .map(([userId, value]) => ({ userId, ...value }))
      .sort((a, b) => b.minutes - a.minutes);
  }, [entries]);

  /**
   * Every agent's entries, grouped and sorted once per load.
   *
   * Three costs used to sit in the render, and they compound: entrySortKey parses an ISO string
   * into a Date and was called from inside the comparator, which runs O(n log n) times; the
   * filter scanned the whole list once per agent; and the sort ran per agent. A 50-entry ticket
   * across 5 agents paid all three on every render, including every keystroke in Hours - the
   * component holds that field's state, so React.memo on the export does nothing for it.
   *
   * Keyed on `entries`, so it survives the renders that have nothing to do with the list.
   * Sorting the grouped arrays rather than `entries` itself, which must not be reordered.
   */
  const entriesByUser = useMemo(() => {
    const keys = new Map(entries.map(e => [e.Id, entrySortKey(e.EntryDate, e.HasTimeOfDay ?? false)]));

    const byUser = new Map<number, TimeEntry[]>();
    entries.forEach(e => {
      const list = byUser.get(e.UserId);
      if (list) list.push(e);
      else byUser.set(e.UserId, [e]);
    });

    // Newest first, Id breaking a tie - the same order the comparator produced inline.
    byUser.forEach(list =>
      list.sort((a, b) => (keys.get(b.Id) ?? 0) - (keys.get(a.Id) ?? 0) || b.Id - a.Id));

    return byUser;
  }, [entries]);

  const canModify = (userId: number) => isAdmin || userId === currentUserId;

  /**
   * Every control that writes is disabled while a save is in flight, and permanently once
   * the ticket is closed. One flag rather than two conditions per control, so a new field
   * added to the form cannot pick up half the rule.
   *
   * Ownership stays separate: the row buttons are hidden from people whose entries they
   * are not, and greyed - not hidden - when the ticket is closed. Hiding them there would
   * make a closed ticket look like it had never had an owner.
   */
  const isLocked = isSaving || isTicketClosed;

  const resetForm = () => {
    setHours('');
    setMinutes('');
    setEntryDate(new Date());
    setStartTime(nowAsTime(restrictToQuarters));
    setEditingEntry(null);
  };

  const toggleExpanded = (userId: number) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });

  /** That agent's own entries, newest first. */
  // Newest first, by the day each row displays rather than by the instant it stores. The
  // server's $orderby cannot make that distinction - it sees one column - so the ordering is
  // settled here, where the flag is known.
  //
  // Id breaks the tie, and it is not decoration. Every date-only row on the same day sorts to
  // the start of that day, so several entries logged on one date are all equal - and a stable
  // sort then leaves them in whatever order arrived, which is the server's $orderby over a
  // column holding the identical value for all of them. That lands as oldest-logged first,
  // inside a list whose days run newest first. Descending Id makes the two agree.
  //
  // Timed rows are unaffected: they already separate by minutesIntoDay, so this only decides
  // two entries sharing a minute, where newest-logged first is the same answer anyway.
  const entriesFor = (userId: number) => entriesByUser.get(userId) ?? EMPTY_ENTRIES;

  /**
   * Digits only, for the Hours and Minutes fields.
   *
   * These were type="number", which brought two browser behaviours nobody asked for: the arrow
   * keys stepped the value, and the mouse wheel stepped it while the field had focus. Scrolling
   * a ticket silently editing a logged duration is a real hazard, not a preference.
   *
   * Blocking both was tried first and was worse. preventDefault on the wheel traps the page
   * scroll inside the field; blurring instead lets the page scroll but costs the agent their
   * cursor, so clicking back in after every scroll. Both are patches on a behaviour we never
   * wanted.
   *
   * type="text" with inputMode="numeric" removes the cause. There is no stepping to block, the
   * caret stays put, the page scrolls normally, and mobile still gets the numeric keypad. What
   * type="number" actually contributed was rejecting non-numeric characters, which this does
   * instead - and validate() below was always the authority on range and integer-ness anyway,
   * so nothing that mattered was resting on the input type.
   */
  const digitsOnly = (value: string) => value.replace(/[^0-9]/g, '');

  /** Returns the total in minutes, or null when the input is not a valid entry. */
  const validate = (): number | null => {
    const parsedHours = hours === '' ? 0 : Number(hours);
    const parsedMinutes = minutes === '' ? 0 : Number(minutes);

    if (!Number.isInteger(parsedHours) || parsedHours < 0 || !Number.isInteger(parsedMinutes) || parsedMinutes < 0) {
      Toast.error(t('time-tracking.error.invalid-number'));
      return null;
    }

    // Hours are deliberately uncapped - agents log whatever they need. 60 is allowed because
    // agents type it, and it rolls up to a clean 1h on save; anything above that belongs in
    // the hours field, so the entry is rejected rather than silently rolled up.
    if (parsedMinutes > MAX_MINUTES) {
      Toast.error(t('time-tracking.error.minutes-range'));
      return null;
    }

    const total = parsedHours * 60 + parsedMinutes;
    if (total <= 0) {
      Toast.error(t('time-tracking.error.empty-entry'));
      return null;
    }

    // Time can be back-dated but not booked against the future. The picker already
    // blocks future days; this catches a value that got in some other way.
    if (entryDate > endOfToday()) {
      Toast.error(t('time-tracking.error.future-date'));
      return null;
    }

    // Not redundant with the picker being replaced below: the date is fixed when the form
    // is first rendered, so a tab left open across midnight would still submit yesterday.
    if (restrictToToday && editingEntry === null && !isToday(entryDate)) {
      Toast.error(t('time-tracking.error.today-only'));
      return null;
    }

    return total;
  };

  /**
   * What to store for the date field, in the shape the tenant's settings imply.
   *
   * Capture off: midnight UTC of the picked day, so every viewer reads back the same day.
   * Capture on: the real instant the work started. The server flattens and flags to match,
   * so a value sent by anything other than this client still lands in a coherent shape.
   */
  const whenToStore = () => {
    if (captureStartTime) {
      // The editor's own clock, which is the clock the form showed them - a timed row is
      // displayed converted, so what they read and what they change are the same frame.
      return toEntryInstant(entryDate, startTime);
    }

    // Capture is off, so there is no Start Time control on the form - which means the agent
    // has not asked to change the time and an existing one must survive. Sending a bare date
    // here would overwrite it with midnight UTC while the row keeps HasTimeOfDay, and the
    // trigger would then read that back on the owner's clock as 5:00 PM the previous day.
    //
    // The stored time-of-day is carried onto whatever date the form now holds. Both sides go
    // through the viewer's clock - fromEntryInstant reads it, toEntryInstant writes it - so an
    // unchanged date round-trips to the same instant, and a changed one keeps the same wall
    // time on the new day.
    if (editingEntry?.HasTimeOfDay)
      return toEntryInstant(entryDate, fromEntryInstant(editingEntry.EntryDate));

    return toEntryDate(entryDate);
  };

  const onSubmit = async () => {
    const total = validate();
    if (total === null) return;

    setIsSaving(true);
    try {
      if (editingEntry === null) {
        // UserId is intentionally omitted - the server assigns it from the caller's
        // identity and ignores anything sent, so time can only be logged as self.
        await api.createTicketTimeEntry(
          { TicketId: ticketId, Minutes: total, EntryDate: whenToStore() },
          clientDay()
        );
        Toast.success(t('time-tracking.success.added'));
      } else {
        // Edits target the one entry that is wrong, so the agent's other dated rows -
        // and the per-date breakdown reporting depends on - survive untouched.
        await api.updateTicketTimeEntry(
          `TicketTimeEntry(${editingEntry.Id})`,
          { Minutes: total, EntryDate: whenToStore() },
          clientDay()
        );
        Toast.success(t('time-tracking.success.updated'));
      }

      resetForm();
      await loadEntries(true);
    } catch {
      Toast.error(editingEntry === null ? t('time-tracking.error.add-failed') : t('time-tracking.error.update-failed'));
    } finally {
      setIsSaving(false);
    }
  };

  /** Loads one entry into the form so it can be corrected. */
  const onEdit = (entry: TimeEntry) => {
    const split = splitDuration(entry.Minutes);
    setHours(split.hours ? String(split.hours) : '');
    setMinutes(split.minutes ? String(split.minutes) : '');
    // Which parts of a stored value are meaningful depends on the row, not on the current
    // setting: a row written before capture was switched on is midnight UTC and has to be
    // read as UTC, while one written after is a real instant and has to be read locally.
    setEntryDate(entry.HasTimeOfDay ? localDayOf(entry.EntryDate) : fromEntryDate(entry.EntryDate));

    // Always set it, never leave it. A date-only row has no time to load, and leaving the field
    // alone would carry over whatever the previously edited row happened to show - so editing a
    // dated entry right after a timed one would offer that other entry's time as if it were this
    // one's. Defaults to now instead, which is what the add form offers.
    setStartTime(entry.HasTimeOfDay ? fromEntryInstant(entry.EntryDate) : nowAsTime(restrictToQuarters));
    setEditingEntry(entry);
  };

  /** Removes every entry this agent logged against the ticket, in one transaction. */
  const onConfirmDeleteAll = async () => {
    if (pendingDelete === null) return;
    setIsSaving(true);
    try {
      await api.setAgentTimeSpent(ticketId, { UserId: pendingDelete.userId, Minutes: 0 });
      if (editingEntry?.UserId === pendingDelete.userId) resetForm();
      Toast.success(t('time-tracking.success.deleted'));
      await loadEntries(true);
    } catch {
      Toast.error(t('time-tracking.error.delete-failed'));
    } finally {
      setPendingDelete(null);
      setIsSaving(false);
    }
  };

  const onConfirmDeleteEntry = async () => {
    if (pendingEntryDelete === null) return;
    setIsSaving(true);
    try {
      await api.deleteTicketTimeEntry(`TicketTimeEntry(${pendingEntryDelete.Id})`);
      if (editingEntry?.Id === pendingEntryDelete.Id) resetForm();
      Toast.success(t('time-tracking.success.deleted'));
      await loadEntries(true);
    } catch {
      Toast.error(t('time-tracking.error.delete-failed'));
    } finally {
      setPendingEntryDelete(null);
      setIsSaving(false);
    }
  };

  if (isLoading) return <Loader className="mt-10" />;

  return (
    <FluentUIThemeConsumer
      render={globalTheme => {
        // Theme tokens rather than a hardcoded white, so the card follows the Teams
        // light/dark theme instead of turning into a white block in dark mode.
        const surface = globalTheme.siteVariables.colorScheme.default.background;
        const divider = globalTheme.siteVariables.colorScheme.default.border ?? 'rgba(0,0,0,0.1)';

        // A <select> and an <input type="time"> are painted by the browser, not by Northstar,
        // so they do not pick up the Teams theme the way the Input components beside them do.
        // The stylesheet tried to cover that with var(--input-background, #f0f0f0) and the
        // variable does not exist - the rest of the product spells it --input-background-color
        // - so the fallback always won. Light mode happened to look right; dark mode got a
        // washed-out light-grey control sitting in a dark bar.
        //
        // colorScheme is the load-bearing half. Background and colour only reach the parts we
        // draw; the clock button on <input type="time">, its spinners and its focus ring are
        // drawn by the browser and ignore both. Telling the browser which scheme it is in makes
        // it render those in dark, which is the difference between a themed field and a themed
        // field with a white clock icon stamped in the corner.
        // The one grey everything disabled in this tab is painted with.
        //
        // It is the value the Northstar Input beside these controls uses for its own disabled
        // text (inputVariables.js: colorDisabled), so a disabled Start Time matches a disabled
        // Hours exactly rather than being a shade chosen to look close.
        const disabledForeground = globalTheme.siteVariables.colorScheme.brand.foregroundDisabled;

        const themedControl = {
          // background2, not the card's own background. surface is the panel behind these
          // fields, so using it made Start Time disappear into the card while the Northstar
          // Inputs beside it sat on their own darker surface. background2 is what the rest of
          // the product uses for an input-like surface.
          background: globalTheme.siteVariables.colorScheme.default.background2,
          // Explicit, so it has to carry the disabled state itself: a colour set inline beats
          // the browser's own greying of a disabled <select> or <input>, so leaving this at
          // the normal foreground left Start Time and Minutes reading as editable next to a
          // properly dimmed Date and Hours.
          color: isLocked ? disabledForeground : globalTheme.siteVariables.colorScheme.default.foreground,
          colorScheme: globalTheme.siteVariables.theme === themeNames.Dark ? 'dark' : 'light',

          // The focus underline, handed to the stylesheet as a variable because :focus-visible
          // cannot be expressed inline. brandBorderFocus1 is not a guess at something that
          // looks close - it is the value the Northstar Input beside these fields uses for the
          // same underline (inputVariables.js: inputFocusBorderColor resolves to
          // colorScheme.brand.borderFocus1), so the two now focus in the same colour in every
          // theme rather than one of them being a fixed purple.
          ['--focus-underline' as string]:
            globalTheme.siteVariables.colorScheme.brand.borderFocus1
        } as React.CSSProperties;

        // The label colour a disabled Clear should have, and does not get on its own.
        //
        // react-teams paints EVERY Button's root colour unconditionally -
        // `color: colorScheme.default.foreground`, with only the boxShadow and the primary
        // override sitting behind its `!text` guard
        // (themes/shared/components/Button/button-styles.js). It is merged after Northstar's
        // own `colorDisabled`, so `disabled` never dims a label anywhere in the product. The
        // primary Add gets away with it because its override is white, which reads as faded
        // against the disabled grey; a secondary in light theme is painted grey[750] and goes
        // on looking perfectly clickable.
        //
        // Inline, not the `styles` prop, because an inline style beats any class the theme
        // emits without depending on fela's merge order.
        //
        // Light only, unlike the icons below. In dark the override paints Clear the same white
        // it paints Add, so the two still read as a pair; dimming one of them alone would
        // break that. Add itself is deliberately left as it is.
        const disabledLabel =
          isLocked && globalTheme.siteVariables.theme === themeNames.Default
            ? { color: disabledForeground }
            : undefined;

        // The row's pencils and trash cans, which have no such excuse in either theme: the
        // same override paints them at full strength, and an icon inherits that through
        // `fill: currentColor`, so a disabled row action looks exactly like a live one.
        const disabledIcon = { color: disabledForeground };

        return (
    // One column: a compact entry bar over a single full-width list. The form is only
    // three short fields, so a half-width card left it swimming in space - and the old
    // right-hand "share of time" list repeated the agent rows verbatim. Layout and
    // breakpoints live in the CSS module.
    <div className={`${Styles.layout} mt-4 mb-4`}>
      <div
        className={Styles.entryBar}
        style={{ backgroundColor: surface, border: `1px solid ${divider}` }}
      >
        {/* Titled to match the Agent Time Spent card below, so the two read as a pair -
            one to enter, one to review - rather than the tab opening on an unlabelled
            row of inputs. Same weight and size as that card's heading. */}
        <Text
          weight="semibold"
          size="large"
          className={Styles.entryHeader}
          content={t('time-tracking.time-entry')}
        />

        <div className={Styles.fieldDate}>
          <FormLabel content={t('time-tracking.date')} />
          {/* With entries restricted to the current day there is one date to choose, so the
              picker is replaced by the date itself - a control offering a single value
              invites the agent to look for others. Editing keeps the entry's own date:
              correcting the hours must not move when the work happened. */}
          {restrictToToday ? (
            <Text className={Styles.fixedDate} content={formatEntryDate(toEntryDate(entryDate))} />
          ) : (
            /* maxDate blocks future days in the calendar itself; validate() rejects a
               future value that arrives any other way. */
            TicketDatePicker(isLocked, entryDate, null, (_e, x) => setEntryDate(x.value), endOfToday())
          )}
        </div>

        {/* Only rendered when the tenant captures it, so a tenant that leaves the setting
            alone sees the form exactly as before.

            Which control depends on the increment restriction, not on preference: a
            quarter-hour list cannot express 2:07, so it is only usable when 2:07 is not
            allowed. Off, the native input - which takes its 12/24-hour presentation from
            the browser and OS, and cannot be told otherwise. */}
        {captureStartTime && (
          <div className={Styles.fieldNarrow}>
            <FormLabel content={t('time-tracking.start-time')} />
            {restrictToQuarters ? (
              /* NestedDropdown, not a native <select> and not Northstar's Dropdown.

                 Native had to go: the list is 96 items and a native popup cannot be bounded.
                 Measured on this markup - a max-height on the select computes and changes
                 nothing, because the popup is drawn by the browser and has no element in the
                 document, and `size` caps the rows only by turning the control into a
                 permanently-open inline listbox, which this form has no vertical room for.
                 Bug 35279.

                 NestedDropdown rather than Northstar's Dropdown because this is a ticket
                 property picker and should look like one: it is what Priority, Category,
                 Status and Type use, so it carries the grey trigger (getBackgroundColor -
                 the same background2 themedControl gives the fields beside it), the hand
                 cursor on the trigger and on every option, a menu capped at 290px, and the
                 flip-above when there is no room below. Northstar's Dropdown gave a white
                 bordered box that read as the odd control on the row. */
              <NestedDropdown
                tabIndex={0}
                disabled={isLocked}
                items={QUARTER_HOUR_OPTIONS}
                dataItem={startTimeOption}
                defaultValue={startTimeOption?.value ?? ''}
                onChange={item => {
                  // Looked up rather than read off the item, because what the form stores is
                  // `time` and what the control knows about is `value`, the rendered label.
                  const picked = QUARTER_HOUR_OPTIONS.find(o => o.id === item?.id);
                  if (picked) setStartTime(picked.time);
                }}
              />
            ) : (
              <input
                type="time"
                className={Styles.plainSelect}
                disabled={isLocked}
                aria-label={t('time-tracking.start-time')}
                style={themedControl}
                value={startTime}
                onChange={e => { if (e.target.value) setStartTime(e.target.value); }}
              />
            )}
          </div>
        )}

        <div className={Styles.fieldNarrow}>
          <FormLabel content={t('time-tracking.hours')} />
          <Input
            fluid
            type="text"
            inputMode="numeric"
            disabled={isLocked}
            value={hours}
            placeholder={t('time-tracking.hours-placeholder')}
            onChange={(_e, x) => setHours(digitsOnly(x.value))}
            input={{ className: Styles.nudgeDown }}
          />
        </div>

        <div className={Styles.fieldNarrow}>
          <FormLabel content={t('time-tracking.minutes')} />
          {/* Quarter-hours only when the tenant asks for it, and then as a list rather than
              a bounded number field - the same rule the start time follows, so an agent who
              cannot start at 2:07 cannot log 2h 07m either. */}
          {restrictToQuarters ? (
            /* The same control as Start Time, for consistency rather than for its list
               behaviour - four options bound themselves. Leaving this a native <select> would
               have put a differently-styled field next to one that had just changed. */
            <NestedDropdown
              tabIndex={0}
              disabled={isLocked}
              items={QUARTER_MINUTE_OPTIONS}
              dataItem={minutesOption}
              defaultValue={minutesOption?.value ?? ''}
              onChange={item => {
                // The id is the minute count itself, so there is nothing to look up.
                if (item) setMinutes(String(item.id));
              }}
            />
          ) : (
            <Input
              fluid
              type="text"
              inputMode="numeric"
              disabled={isLocked}
              value={minutes}
              placeholder={t('time-tracking.minutes-placeholder')}
              onChange={(_e, x) => setMinutes(digitsOnly(x.value))}
              input={{ className: Styles.nudgeDown }}
            />
          )}
        </div>

        <div className={Styles.spacer} />

        {/* Grouped so the pair wraps as a unit. As separate flex children they wrapped
            independently, stranding one button on the line above the other as the tab
            narrowed. The edit banner rides along because it explains what these two
            buttons will do. */}
        <div className={Styles.actions}>
          {/* The bar sits at the top of the tab, far from the row whose pencil was
              clicked, so without this there is nothing to say you are editing rather than
              adding. */}
          {editingEntry !== null && (
            <Text
              size="small"
              className={Styles.editBanner}
              styles={{
                backgroundColor: globalTheme.siteVariables.colorScheme.brand.background1,
                color: globalTheme.siteVariables.colorScheme.brand.foreground1
              }}
              content={t('time-tracking.editing-entry', {
                name: displayName(editingEntry.User),
                date: formatEntryWhen(editingEntry.EntryDate, editingEntry.HasTimeOfDay ?? false)
              })}
            />
          )}

          {/* "Clear" when adding, "Cancel" when editing - mid-edit the button abandons the
              edit rather than blanking a form you were filling in. */}
          <Button
            content={editingEntry === null ? t('time-tracking.clear') : t('ticket-details.edit-ticket-view.cancel')}
            onClick={resetForm}
            disabled={isLocked}
            style={disabledLabel}
          />
          {/* No `loading` prop - the ticket property Save button just disables while in
              flight, and its spinner-free behaviour is what this should match. */}
          <Button
            primary
            disabled={isLocked}
            content={editingEntry === null ? t('time-tracking.add') : t('time-tracking.update')}
            onClick={onSubmit}
          />
        </div>
      </div>

      <Flex
        column
        className={Styles.card}
        styles={{
          backgroundColor: surface,
          border: `1px solid ${divider}`,
          // minHeight: 0 is what actually lets the inner scroll area shrink inside a
          // flex parent instead of pushing the card past the tab.
          minHeight: 0
        }}
      >
          {/* The total belongs here rather than on the entry form - it summarises this
              list, it is not a property of the input. */}
          {/* This header keeps its rule while the Time Entry card above does not, and the
              asymmetry is deliberate: the list below scrolls, so the rule is what makes a
              clipped row read as content passing under a fixed header rather than as a
              row that has been cut off. Nothing scrolls in the entry form, so a rule
              there would be weight without a job. */}
          <Flex
            space="between"
            vAlign="center"
            gap="gap.small"
            className="px-4 py-3"
            styles={{ borderBottom: `1px solid ${divider}` }}
          >
            <Text weight="semibold" size="large" content={t('time-tracking.agent-time-spent')} />
            <Flex gap="gap.smaller" vAlign="center" styles={{ whiteSpace: 'nowrap' }}>
              <MeetingTimeIcon outline size="small" />
              <Text>
                <Trans
                  i18nKey="time-tracking.total-time"
                  components={{ dur: <DurationText minutes={totalMinutes} /> }}
                />
              </Text>
            </Flex>
          </Flex>

          <Flex column gap="gap.small" className={`px-4 py-4 ${Styles.entryList}`}>
            {/* The shared empty state, same as Approvals and Related Tickets on this
                ticket - the illustration and sizing come from EmptyData rather than
                being styled here, so this pane matches its siblings. */}
            {/* EmptyData centres itself, which strands it mid-card when the card fills
                the whole tab. Two weightless spacers put it a quarter of the way down
                instead. Flex ratios rather than a percentage padding, because a
                percentage padding resolves against the container's WIDTH, not its
                height - on a card this wide that would be a ~365px gap, not a quarter
                of anything. Fixed pixels were the other option but they crowd the
                header on a short window and look pinned on a tall one. */}
            {perPerson.length === 0 && (
              <Flex column styles={{ flexGrow: 1, minHeight: 0 }}>
                <div style={{ flex: '1 1 0' }} />
                <EmptyData
                  headerText={t('time-tracking.no-entries')}
                  subheaderText={t('time-tracking.no-entries-hint')}
                  SVGIcon={<EmptySVG width={75} height={75} />}
                  style={{ height: 'auto', flex: 'none' }}
                />
                <div style={{ flex: '3 1 0' }} />
              </Flex>
            )}

            {/* One row per agent, collapsed by default, so the pane stays the same
                length however much time accumulates. Expanding reveals that agent's
                individual dated entries - editing targets a specific one rather than the
                total, which is what keeps the per-date history intact. */}
            {perPerson.map((person, rank) => {
              const isOpen = expanded.has(person.userId);
              const mine = canModify(person.userId);

              return (
                // The chevron and avatar sit in their own fixed column, and everything
                // else - name, bar, dated entries - lives in the column beside them. That
                // way the bar and the entry rows line up under the name automatically,
                // rather than being nudged into place with a hardcoded indent that breaks
                // the moment the avatar size changes.
                <Flex key={person.userId} gap="gap.small">
                  <Flex
                    gap="gap.small"
                    vAlign="center"
                    styles={{ cursor: 'pointer', flex: 'none', alignSelf: 'flex-start', height: '32px' }}
                    onClick={() => toggleExpanded(person.userId)}
                  >
                    {isOpen ? <ChevronDownIcon size="smaller" /> : <ChevronEndIcon size="smaller" />}
                    {/* UserAvatar resolves the cached profile photo from the AAD object
                        id or email. A bare Avatar with only a name renders initials and
                        never loads a picture. */}
                    <UserAvatar
                      idOrUpn={person.idOrUpn}
                      showPresence={false}
                      avatarProps={{ name: person.name, size: 'small' }}
                    />
                  </Flex>

                  <Flex column gap="gap.smaller" styles={{ flexGrow: 1, minWidth: 0 }}>
                    <Flex gap="gap.small" space="between" vAlign="center">
                      {/* Name and bar share a column so the bar stops where the delete
                          button begins instead of running underneath it. */}
                      <Flex
                        column
                        gap="gap.smaller"
                        styles={{ flexGrow: 1, minWidth: 0, cursor: 'pointer' }}
                        onClick={() => toggleExpanded(person.userId)}
                      >
                        <Text truncated>
                          <Trans
                            i18nKey="time-tracking.person-spent"
                            values={{ name: person.name }}
                            components={{ dur: <DurationText minutes={person.minutes} /> }}
                          />
                        </Text>
                        <ShareBar minutes={person.minutes} totalMinutes={totalMinutes} rank={rank} label={person.name} />
                      </Flex>

                      {/* Delete on the agent row removes the lot - unambiguous in a way
                          editing a total is not, so it stays at this level.

                          Always rendered, hidden rather than omitted when the caller has
                          no rights. Omitting it made the name column wider on rows
                          without a button, so the bars came out different lengths and
                          could not be compared. Hiding reserves exactly the right width
                          without a hardcoded guess at the button's size. */}
                      <Flex
                        styles={{ flex: 'none', visibility: mine ? 'visible' : 'hidden' }}
                        aria-hidden={!mine}
                      >
                        <Button
                          text
                          iconOnly
                          size="small"
                          disabled={isLocked || !mine}
                          style={isLocked || !mine ? disabledIcon : undefined}
                          tabIndex={mine ? undefined : -1}
                          icon={<TrashCanIcon outline />}
                          title={mine ? t('time-tracking.delete-all') : undefined}
                          onClick={() =>
                            mine && setPendingDelete({ userId: person.userId, name: person.name })
                          }
                        />
                      </Flex>
                    </Flex>

                    {isOpen &&
                    entriesFor(person.userId).map(entry => (
                      <Flex
                        key={entry.Id}
                        gap="gap.small"
                        vAlign="center"
                        space="between"
                      >
                        {/* size="small" alone carries the hierarchy against the agent row
                            above, which is default size. Northstar timestamp styling was
                            doing it a second time via colour, and that muted tone is too
                            faint to read against the dark theme background. */}
                        <Text size="small" truncated>
                          {formatEntryWhen(entry.EntryDate, entry.HasTimeOfDay ?? false)}
                          {' · '}
                          <DurationText minutes={entry.Minutes} />
                        </Text>
                        {/* Hidden rather than omitted, the same as the agent row above.
                            Omitting collapsed the row to the height of its text, so an
                            agent's own entries sat at a different rhythm from everyone
                            else's in the same list - visible to any non-admin looking at
                            a ticket several people have logged time against, which is
                            what this breakdown is for. */}
                        <Flex
                          gap="gap.smaller"
                          styles={{ flex: 'none', visibility: mine ? 'visible' : 'hidden' }}
                          aria-hidden={!mine}
                        >
                          <Button
                            text
                            iconOnly
                            size="small"
                            disabled={isLocked || !mine}
                            style={isLocked || !mine ? disabledIcon : undefined}
                            tabIndex={mine ? undefined : -1}
                            icon={<EditIcon outline />}
                            title={mine ? t('time-tracking.edit') : undefined}
                            onClick={() => mine && onEdit(entry)}
                          />
                          <Button
                            text
                            iconOnly
                            size="small"
                            disabled={isLocked || !mine}
                            style={isLocked || !mine ? disabledIcon : undefined}
                            tabIndex={mine ? undefined : -1}
                            icon={<TrashCanIcon outline />}
                            title={mine ? t('time-tracking.delete') : undefined}
                            onClick={() => mine && setPendingEntryDelete(entry)}
                          />
                        </Flex>
                      </Flex>
                    ))}
                  </Flex>
                </Flex>
              );
            })}
          </Flex>
      </Flex>

      {/* Removing an agent's whole set of entries asks first rather than acting on a
          single click. */}
      <Dialog
        open={pendingDelete !== null}
        onCancel={() => setPendingDelete(null)}
        onConfirm={onConfirmDeleteAll}
        className={AppCSS.confirmDialog}
        cancelButton={t('ticket-details.edit-ticket-view.cancel')}
        confirmButton={t('time-tracking.delete')}
        header={t('time-tracking.delete-confirm-header')}
        content={
          // "your time spent" only reads correctly when it is your own. An Admin
          // clearing someone else's needs to see whose.
          pendingDelete?.userId === currentUserId
            ? t('time-tracking.delete-confirm-body')
            : t('time-tracking.delete-confirm-body-other', { name: pendingDelete?.name ?? '' })
        }
      />

      <Dialog
        open={pendingEntryDelete !== null}
        onCancel={() => setPendingEntryDelete(null)}
        onConfirm={onConfirmDeleteEntry}
        className={AppCSS.confirmDialog}
        cancelButton={t('ticket-details.edit-ticket-view.cancel')}
        confirmButton={t('time-tracking.delete')}
        header={t('time-tracking.delete-entry-confirm-header')}
        content={
          pendingEntryDelete?.UserId === currentUserId
            ? t('time-tracking.delete-entry-confirm-body')
            : t('time-tracking.delete-entry-confirm-body-other', {
                name: displayName(pendingEntryDelete?.User)
              })
        }
      />
    </div>
        );
      }}
    />
  );
};

export const TicketTimeTracking = React.memo(TicketTimeTrackingImpl);
